import { Container } from '@/components/ui/Grid';
import styles from './product.module.scss';
import { CaretIcon, ModalCloseButton, Select } from '@frabbit/react-responsive-select';
import { useRouter } from 'next/router';
import { motion } from 'framer-motion';
import EditorOutput from '@/components/common/EditorOutput';
import MotionHeader from '@/components/ui/MotionHeader';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useKeenSlider } from 'keen-slider/react';
import { useTranslation } from 'next-i18next';

const variants = {
  initial: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 1.3,
      delay: 1,
    },
  },
  hidden: { opacity: 0 },
};

const Product = ({ data }) => {
  const { t } = useTranslation(['common', 'product']);
  const router = useRouter();

  const categories = data.categories;
  const category = data.category && categories.find(c => c.id === data.category);
  const [details, setDetails] = useState(null);

  const getSrcSet = useCallback(img => {
    return Object.entries(img.thumbnails)
      .sort(([key]) => -key)
      .reduce(
        (acc, curr) => (!!acc.length ? `${curr[1]} ${curr[0]}w, ${acc}` : `${curr[1]} ${curr[0]}w`),
        ''
      );
  }, []);

  const options = useMemo(() => {
    return [
      ...categories.reduce((acc, category) => {
        return [
          ...acc,
          {
            text: category.title,
            value: `category__${category.id}`,
            markup: <span className={styles.selectGroupName}>{category.title}</span>,
          },
          ...category.products.map(child => ({
            value: `product__${child.id}`,
            text: child.title,
            markup: (
              <div className={styles.selectItem}>
                {child.icon && <img src={child.icon} alt={child.title} />}
                <span>{child.title}</span>
              </div>
            ),
          })),
        ];
      }, []),
    ];
  }, [categories]);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [sliderRef, slider] = useKeenSlider({
    initial: 0,
    loop: true,
    duration: 5000,
    controls: false,
    slideChanged(s) {
      setCurrentSlide(s.details().relativeSlide);
    },
    move(s) {
      setDetails(s.details());
    },
  });

  useEffect(() => {
    const interval = setInterval(() => slider.next(), 7000);
    return () => clearInterval(interval);
  }, [slider]);

  const positionStyle = useCallback(
    idx => {
      if (!details) return {};
      return {
        opacity: details.positions[idx].portion,
        zIndex: '-1',
      };
    },
    [details?.positions]
  );

  return (
    <Container>
      <div className={styles.header}>
        <MotionHeader>{t('Продукция')}</MotionHeader>
      </div>
      <div className={styles.wrapper}>
        <div className={styles.product}>
          <div className={styles.info}>
            <div className={styles.left}>
              <motion.h1
                initial={{ opacity: 0, y: '15%' }}
                animate={{ opacity: 1, y: 0, transition: { duration: 1, delay: 1 } }}
                exit={{ opacity: 0, y: '-15%' }}
                className={styles.hiddenXs}
              >
                {data.title}
              </motion.h1>
              {data.text && <EditorOutput data={data.text} />}

              {data.presentation && <motion.p initial={{
                                                  opacity: 0,
                                                  y: '15%',
                                               }}
                                               animate={{
                                                 opacity: 1,
                                                 y: 0,
                                                 transition: { duration: 0.8, delay: 1 },
                                               }}
                                               exit={{
                                                 opacity: 0,
                                                 y: '-15%',
                                                 transition: { duration: 0.3 },
                                               }} ><a href={data.presentation} target="_blank">{t('Узнать больше')}</a></motion.p>}

            </div>

            <div className={styles.right}>
              <motion.h1
                initial={{ opacity: 0, y: '15%' }}
                animate={{ opacity: 1, y: 0, transition: { duration: 1, delay: 1 } }}
                exit={{ opacity: 0, y: '-15%' }}
                className={styles.hiddenDesktopHeader}
              >
                {data.title}
              </motion.h1>
              <motion.div
                initial="initial"
                animate="visible"
                exit="hidden"
                variants={variants}
                className={styles.selectWrapper}
              >
                <Select
                  name="category"
                  modalCloseButton={<ModalCloseButton />}
                  options={options}
                  caretIcon={<CaretIcon />}
                  selectedValue={`product__${data.id}`}
                  onChange={newValue => {
                    const [value_type, value] = newValue.value.split('__');
                    if (value_type === 'category') {
                      router.push(`/catalog/category/${value}`);
                    } else {
                      router.push(`/catalog/product/${value}`);
                    }
                  }}
                />
              </motion.div>

              <motion.div
                className={styles.slider}
                initial={{
                  opacity: 0,
                }}
                animate={{
                  opacity: 1,
                  transition: { duration: 2.3, delay: 0.8 },
                }}
                exit={{
                  opacity: 0,
                  transition: { duration: 0.6 },
                }}
              >
                <div className={styles.sliderWrapper}>
                  <div ref={sliderRef} className="keen-slider">
                    {data.image.map((slide, idx) => (
                      <div
                        key={slide.id}
                        className="keen-slider__slide"
                        style={{ ...positionStyle(idx), zIndex: 1 }}
                      >
                        <div className={styles.slide}>
                          <img
                            src={slide.image}
                            srcSet={getSrcSet(slide)}
                            alt={slide.name}
                            sizes="50vw"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {slider && data?.image?.length > 1 && (
                  <div className={styles.dots}>
                    {[...Array(slider.details().size).keys()].map((idx, k) => {
                      const delay = k * 0.2 + 1;
                      return (
                        <motion.div
                          key={idx}
                          onClick={() => {
                            slider.moveToSlideRelative(idx);
                          }}
                          className={clsx(styles.dot, { [styles.dotActive]: currentSlide === idx })}
                          initial={{
                            opacity: 0,
                          }}
                          animate={{
                            opacity: 1,
                            x: 0,
                            transition: { duration: 0.8, delay },
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </motion.div>
            </div>
          </div>
          <motion.div
            initial="initial"
            animate="visible"
            exit="hidden"
            variants={variants}
            className={styles.properties}
          >
            <div className={styles.propsLeft}>
              <table>
                <tbody>
                  {category && category.title && (
                    <tr>
                      <th>{t('Категория')}</th>
                      <td>{category.title}</td>
                    </tr>
                  )}
                  {!!data.country?.length && (
                    <tr>
                      <th>{t('Страна')}</th>
                      <td>{data.country.map(({ title }) => title).join(', ')}</td>
                    </tr>
                  )}
                  {!!data.processing?.length && (
                    <tr>
                      <th>{t('Тип разделки')}</th>
                      <td>{data.processing.map(({ title }) => title).join(', ')}</td>
                    </tr>
                  )}
                  {!!data.segment?.length && (
                    <tr>
                      <th>{t('Сегмент')}</th>
                      <td>{data.segment.map(({ title }) => title).join(', ')}</td>
                    </tr>
                  )}
                  {!!data.size_range?.length && (
                    <tr>
                      <th>{t('Размерный ряд')}</th>
                      <td>{data.size_range.map(({ title }) => title).join(', ')}</td>
                    </tr>
                  )}
                  {data.eav &&
                    data.eav.length > 0 &&
                    data.eav.map(e => {
                      return (
                        <tr key={e.id}>
                          <th>{e.asset.asset}</th>
                          <td>{e.value.value}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
            {data.features && data.features.length > 0 && (
              <div className={styles.propsRight}>
                {data.features.map(f => (
                  <div key={f.id} className={styles.badge}>
                    {f.icon && <img src={f.icon} alt={f.title} />}
                    <span>
                      <b>{f.title}</b>
                      <br /> {f.description}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </Container>
  );
};

export default Product;
